import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/libs/utils";
import { CLIP_SCRIM } from "@/constants/gradients";
import { useTrans } from "@/utils/useTrans";
import { useGameClips } from "@/services/banner/useBannerService";
import { Skeleton } from "@/v2-components/ui/skeleton";
import SectionHeading from "@/components/home/SectionHeading";

/**
 * "TactSuit를 체감한 플레이어들의 반응" — 피그마 28983:91463 + 디자이너 공유 HTML.
 *
 * 시안의 트랙은 3168px(432x7 + 24x6)로 1440 컨테이너를 양쪽으로 넘겨 흘린다. 그래서 섹션 좌우
 * 패딩 없이 **풀블리드**로 두고, 트랙을 가운데로 스크롤해 둔 상태에서 시작한다.
 *
 * ⚠️ 시안 HTML 은 `transform: translateX()` 로 트랙을 밀지만 그렇게 하면 휠·스와이프·Tab 이동이
 * 죽는다. 여기서는 `overflow-x:auto` 를 유지하고 **초기 스크롤 위치만 가운데로 맞춘다** —
 * 보이는 결과는 같고 접근성은 살아있다. 스크롤바만 감춘다(`scrollbar-none`, globals.css).
 *
 * ⚠️ 재생 규칙이 위 `PlayEveryWay` 와 **반대**다(디자이너 확인).
 *   - 데스크톱: 정지 상태로 두고 **호버한 카드만** 재생
 *   - 모바일: 호버가 없으므로 **상시 재생**
 */

/** 정지 상태에서 세워 둘 프레임(초). 0 이면 브라우저에 따라 검은 화면이 잡힌다. */
export const STILL_SECONDS = 0.1;
/** 브라우저는 seek 안착점을 **디코딩 가능한 프레임 경계로 조정**하므로 `currentTime` 이
 *  정확히 `STILL_SECONDS` 로 돌아오지 않는다(25fps → 0.08/0.12, 24fps → 0.083/0.125).
 *  등호 비교를 쓰면 그런 클립에서 아래 가드가 영영 발동하지 않는다. 20fps 까지 덮는 창. */
export const FRAME_TOLERANCE = 0.05;
/** 카드 432 + gap 24 — 화살표 1회 이동량(시안). 실제로는 카드 폭을 실측해 쓴다. */
const GAP_PX = 24;
const DESKTOP_QUERY = "(min-width: 1024px)";

/**
 * `matchMedia` 는 jsdom 에 없다. 없으면 **모바일(상시 재생)** 로 떨어뜨린다 —
 * 재생이 안 되는 쪽보다 재생되는 쪽이 안전하다.
 */
const isDesktopViewport = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(DESKTOP_QUERY).matches;

/**
 * ⚠️ `play()` 는 Promise 를 돌려주지 **않을 수도** 있다(구형 브라우저·jsdom).
 * 반환값을 확인하지 않고 `.catch()` 를 붙이면 그 자리에서 TypeError 가 난다.
 */
const play = (video: HTMLVideoElement) => {
  const started = video.play?.();
  if (started && typeof started.catch === "function") started.catch(() => {});
};

/**
 * 노출 순서 고정 — 기획 요청(2026-08-14): Fortnite 를 맨 앞, Counter-Strike 2 를 두 번째로.
 *
 * ⚠️ **제목이 아니라 영상 URL 로 식별한다.** prod 배너 데이터는 `title` 이 전부 비어 있어서
 * (dev 는 채워져 있다) 제목으로 맞추면 prod 에서만 조용히 어긋난다.
 */
const PINNED_VIDEO_KEYS = ["fortnite.mp4", "cs_review"];

/** dev 배너 API 에는 CS2 가 없다(prod 에는 있다). 없을 때만 채운다. */
const FALLBACK_CLIPS = [
  {
    videoUrl:
      "https://cdn.bhaptics.com/Banners/game%20clip/cs_review%20video.mp4",
    imageUrl: "",
    title: "Counter-Strike 2",
    link: "https://www.youtube.com/watch?v=NyXFa-sXqW8",
  },
];

/** `%20` 과 실제 공백이 섞여 오므로(같은 파일을 가리킨다) 디코드해서 비교한다. */
const videoKey = (url: string) => {
  try {
    return decodeURIComponent(url).trim().toLowerCase();
  } catch {
    return url.trim().toLowerCase();
  }
};

export default function PlayerReactions({ className }: { className?: string }) {
  const { t } = useTrans(["index"]);
  const { data, isLoading } = useGameClips();

  const items = useMemo(() => {
    const fromApi = (data ?? []).map((clip) => ({
      videoSrc: clip.videoUrl,
      imageSrc: clip.imageUrl,
      // ⚠️ 캡션은 `clip.game.name` 이 정본이다. `clip.title` 은 **prod 에서 전부 비어 있다**.
      title: clip.game?.name || clip.title || "",
      link: clip.link,
    }));
    // 관리툴에 같은 영상이 들어오면 코드 쪽 항목은 쓰지 않는다.
    const missing = FALLBACK_CLIPS.filter(
      (f) =>
        !fromApi.some((c) => videoKey(c.videoSrc) === videoKey(f.videoUrl)),
    ).map((f) => ({
      videoSrc: f.videoUrl,
      imageSrc: f.imageUrl,
      title: f.title,
      link: f.link,
    }));

    const all = [...fromApi, ...missing];
    // 고정 순서 항목을 앞으로. 나머지는 원래 순서를 유지한다(안정 정렬).
    const rank = (videoSrc: string) => {
      const key = videoKey(videoSrc);
      const i = PINNED_VIDEO_KEYS.findIndex((p) => key.includes(p));
      return i === -1 ? PINNED_VIDEO_KEYS.length : i;
    };
    return all
      .map((item, index) => ({ item, index }))
      .sort(
        (a, b) =>
          rank(a.item.videoSrc) - rank(b.item.videoSrc) || a.index - b.index,
      )
      .map(({ item }) => item);
  }, [data]);

  return (
    <section className={cn("w-full", className)}>
      <div className="px-4 laptop:px-8">
        <SectionHeading
          title={t("Player Reactions with TactSuit")}
          description={t(
            "See real reactions to haptic feedback across PC, VR, sim racing, and streaming.",
          )}
        />
      </div>

      {isLoading ? (
        <div className="mt-6 px-4 laptop:mt-8 laptop:px-8">
          <Skeleton className="mx-auto h-[135px] max-w-screen-max rounded-xl laptop:h-[243px] laptop:rounded-2xl" />
        </div>
      ) : (
        <Strip items={items} />
      )}
    </section>
  );
}

type Item = {
  videoSrc: string;
  imageSrc: string;
  title: string;
  link: string;
};

function Strip({ items }: { items: Item[] }) {
  const { t } = useTrans(["index"]);
  const stripRef = useRef<HTMLDivElement | null>(null);
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const sync = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const max = strip.scrollWidth - strip.clientWidth;
    const hidePrev = strip.scrollLeft <= 1;
    // 넘칠 게 없으면 max=0 이라 양쪽 다 숨는다.
    const hideNext = strip.scrollLeft >= max - 1;

    // 포커스를 쥔 버튼을 그냥 숨기면 focus 가 body 로 떨어져 다음 Tab 이 문서 맨 앞에서
    // 다시 시작한다. 반대편이 남아 있으면 그쪽으로 넘겨준다.
    if (hidePrev && document.activeElement === prevRef.current && !hideNext) {
      nextRef.current?.focus();
    }
    if (hideNext && document.activeElement === nextRef.current && !hidePrev) {
      prevRef.current?.focus();
    }

    setAtStart(hidePrev);
    setAtEnd(hideNext);
  }, []);

  // 시안처럼 트랙이 좌우로 균등하게 흘러넘친 상태에서 시작한다(데스크톱만).
  // 모바일은 첫 카드부터 보여야 하므로 건드리지 않는다.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || items.length === 0) return;
    if (isDesktopViewport()) {
      strip.scrollLeft = (strip.scrollWidth - strip.clientWidth) / 2;
    }
    sync();
  }, [items.length, sync]);

  useEffect(() => {
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, [sync]);

  const go = (direction: 1 | -1) => {
    const strip = stripRef.current;
    if (!strip) return;
    // 카드 폭을 실측해 gap 을 더한다 — 뷰포트에 따라 카드가 줄어도 맞는다.
    const card = strip.querySelector("[data-clip]");
    const step = card
      ? card.getBoundingClientRect().width + GAP_PX
      : strip.clientWidth * 0.8;
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    strip.scrollBy({
      left: direction * step,
      behavior: reduce ? "auto" : "smooth",
    });
  };

  return (
    <div className="relative mt-6 laptop:mt-8">
      <NavButton
        ref={prevRef}
        side="prev"
        hidden={atStart}
        label={t("Previous videos")}
        onClick={() => go(-1)}
      />
      <NavButton
        ref={nextRef}
        side="next"
        hidden={atEnd}
        label={t("Next videos")}
        onClick={() => go(1)}
      />

      <div
        ref={stripRef}
        onScroll={sync}
        // 스크롤바만 감춘다. overflow-x 는 유지해야 휠·스와이프·탭 이동이 살아있다.
        className="scrollbar-none overflow-x-auto"
      >
        <div className="flex w-max gap-3 px-4 laptop:gap-6 laptop:px-0">
          {items.map((item, index) => (
            <Clip key={`${index}-${item.title}`} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Clip({ item }: { item: Item }) {
  const { t } = useTrans(["index"]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPortrait, setIsPortrait] = useState(false);

  /** 데스크톱 = 정지 + 첫 프레임, 모바일 = 상시 재생. 렌더가 아니라 명령형으로 맞춘다
   *  (SSR/CSR 로 갈리는 값을 렌더에 넣으면 hydration 이 어긋난다). */
  const applyPlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!isDesktopViewport()) {
      play(video);
      return;
    }
    video.pause();
    // ⚠️ 이미 정지 프레임이면 되감지 않는다. `currentTime` 대입은 seek 이고 seek 은 다시
    // `canplay` 를 던지므로, 무조건 되감으면 이벤트 → 되감기 → 이벤트 루프가 된다.
    // 창은 **양쪽**이어야 한다 — `t <= STILL + tol` 한쪽만 보면 아직 아무것도 안 한 `t = 0`
    // 까지 걸러져 **첫 seek 자체가 사라진다**(실측: 전 클립이 t=0 에 멈춰 검은 프레임).
    if (Math.abs(video.currentTime - STILL_SECONDS) <= FRAME_TOLERANCE) return;
    try {
      video.currentTime = STILL_SECONDS;
    } catch {
      /* 메타데이터 전이면 무시 — loadedmetadata 에서 다시 세운다 */
    }
  }, []);

  /**
   * 모바일 자동재생 보강 **전용**. 마운트 직후 `play()` 는 로딩 중이면 거절되므로 데이터가
   * 준비된 시점에 다시 시도한다.
   *
   * ⚠️ 여기서 `applyPlayback` 을 쓰면 안 된다. 데스크톱 분기의 pause + 되감기가 호버 재생을
   * 즉시 되돌리고, 그 되감기가 `canplay` 를 다시 던져 **무한 루프**가 된다.
   * (실측: play → playing → pause → seeking → seeked → canplay → pause … 수천 회 반복,
   *  호버해도 `paused: true` 로 남았다)
   */
  const resumeOnMobile = useCallback(() => {
    const video = videoRef.current;
    if (!video || isDesktopViewport()) return;
    play(video);
  }, []);

  useEffect(() => {
    applyPlayback();
    const query =
      typeof window.matchMedia === "function"
        ? window.matchMedia(DESKTOP_QUERY)
        : null;
    query?.addEventListener("change", applyPlayback);
    // 문서가 숨겨지면 브라우저가 재생을 멈춘다 — 돌아왔을 때 되살린다.
    document.addEventListener("visibilitychange", applyPlayback);
    return () => {
      query?.removeEventListener("change", applyPlayback);
      document.removeEventListener("visibilitychange", applyPlayback);
    };
  }, [applyPlayback]);

  return (
    <a
      data-clip
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      // i18next 보간을 쓴다. `.replace()` 로 직접 끼우면 i18next 가 먼저 `{{title}}` 을
      // 빈 값으로 치환해 버려서 제목이 사라진다.
      aria-label={t("Watch {{title}} review video", { title: item.title })}
      onMouseEnter={() => {
        const video = videoRef.current;
        if (!video || !isDesktopViewport()) return;
        play(video);
      }}
      onMouseLeave={applyPlayback}
      className={cn(
        "relative block h-[135px] w-[240px] shrink-0 overflow-hidden rounded-xl",
        "laptop:h-[243px] laptop:w-[432px] laptop:rounded-2xl",
        // 세로 영상은 잘리지 않게 contain 처리하므로 여백이 생긴다 — 검은 배경으로 받는다.
        isPortrait ? "bg-black" : "bg-coolgray-10",
      )}
    >
      <video
        ref={videoRef}
        loop
        muted
        playsInline
        preload="metadata"
        poster={item.imageSrc || undefined}
        onLoadedMetadata={(e) => {
          const video = e.currentTarget;
          if (video.videoWidth && video.videoHeight) {
            setIsPortrait(video.videoWidth / video.videoHeight < 1);
          }
          applyPlayback();
        }}
        // ⚠️ `autoPlay` 속성을 쓰지 않는다(데스크톱은 정지 상태로 시작). 대신 모바일에서만
        // 데이터가 준비된 시점에 다시 `play()` 한다 — 데스크톱은 손대지 않는다(위 주석 참조).
        onCanPlay={resumeOnMobile}
        className={cn(
          "size-full",
          isPortrait ? "object-contain" : "object-cover",
        )}
      >
        <source src={item.videoSrc} type="video/mp4" />
      </video>
      <div
        className="absolute inset-0"
        style={{ backgroundImage: CLIP_SCRIM }}
        aria-hidden
      />
      <span className="absolute bottom-3 left-3 text-base font-600 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)]">
        {item.title}
      </span>
    </a>
  );
}

const NavButton = ({
  ref,
  side,
  hidden,
  label,
  onClick,
}: {
  ref: React.RefObject<HTMLButtonElement | null>;
  side: "prev" | "next";
  hidden: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    ref={ref}
    type="button"
    aria-label={label}
    onClick={onClick}
    // ⚠️ `hidden` 속성만으로는 안 된다 — flex/grid 컨테이너의 display 가 이를 덮는다.
    // 모바일은 네이티브 스크롤로 넘기므로 버튼 자체를 두지 않는다(시안).
    className={cn(
      "absolute top-1/2 z-[5] size-11 -translate-y-1/2 place-items-center rounded-full",
      "bg-white/[0.94] text-coolgray-90 shadow-[0_2px_8px_rgba(0,0,0,0.16)] transition hover:bg-white",
      side === "prev" ? "left-5" : "right-5",
      hidden ? "hidden" : "hidden laptop:grid",
    )}
  >
    <svg
      viewBox="0 0 20 20"
      fill="none"
      className={cn("size-5", side === "prev" && "rotate-180")}
      aria-hidden
    >
      <path
        d="M8.4 6.2L12.2 10L8.4 13.8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);
